import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import { Translation, getI18n } from 'react-i18next';
import { Affix, Card, Col, DatePicker, Form, Row, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import moment from 'moment';

import BodyInput from './BodyInput';
import PublicStatusInput from './PublicStatusInput';
import UserInput from '../../users/containers/UserInputContainer';
import message from '../../../elements/lib/MessageWrapper';
import FormMetadata from '../../../elements/components/FormMetadata';
import { pathTo } from '../../../Routes';
import Format from '../../../../../lib/Format';
import Logger from '../../../../../lib/Logger';
import Config from '../../../../../Config';
import ResourceInput from '../../resources/containers/ResourceInputContainer';
import UserCareGuidesInput from '../../users/containers/UserCareGuidesInputContainer';
import UserPartnershipInput from '../../users/containers/UserPartnershipInputContainer';
import { schema } from '../../../../../state/schema';
import store from '../../../../../state/store';
import { denormalize } from 'normalizr';

const ResourceNoteForm = ({
  id,
  data,
  errors,
  load,
  destroyForm,
  isLoading,
  isSubmitting,
  created_id,
  ...props
}) => {
  const state = store.getState();
  const [redirectTo, setRedirectTo] = useState(null);
  const [careGuideIds, setCareGuideIds] = useState([]);
  const [partnershipId, setPartnershipId] = useState(null);
  const [firstLoad, setFirstLoad] = useState(true);

  const [form] = Form.useForm();

  // form column settings
  const layout = {
    main: {
      labelCol: { span: 5 },
      wrapperCol: { span: 19 },
    },
  };

  // load record data from API
  useEffect(() => {
    if (id) {
      load(id);
    }
  }, [id, load]);

  // update input values when new data is available
  const dataString = JSON.stringify(data);
  useEffect(() => {
    if (id && !isSubmitting) {
      const dataObj = JSON.parse(dataString);
      onChangeResource(dataObj['resource_id']);
      dataObj['published_at'] = dataObj['published_at']
        ? moment(dataObj['published_at'], Config.get('API_DATETIME_FORMAT'))
        : moment();
      dataObj['edited_at'] = dataObj['edited_at']
        ? moment(dataObj['edited_at'], Config.get('API_DATETIME_FORMAT'))
        : null;
      form.setFieldsValue(dataObj);
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dataString, isSubmitting, id]);

  // handle errors reported by API
  useEffect(() => {
    let firstFieldName = '';
    for (const field in errors) {
      form.setFields([{ name: field, errors: errors[field] }]);
      if (firstFieldName === '') {
        firstFieldName = field;
      }
    }
    form.scrollToField(firstFieldName);
  }, [form, errors]);
  // redirect add form to edit form on successful create action
  useEffect(() => {
    if (created_id) {
      setRedirectTo(pathTo('ResourceNoteEditScreen', { id: created_id }));
    }
    return () => {
      destroyForm();
    };
  }, [created_id, setRedirectTo, destroyForm]);

  // submit data handler
  const submitData = async (values) => {
    Logger.log('debug', `ResourceNoteForm.submitData(###)`);

    // API POST/PUT payload
    let payload = {};
    for (const input of Object.keys(data)) {
      if (input in values) {
        // transform profile refs to UUIDs
        if (['profile'].includes(input)) {
          payload['user_profile_id'] = values[input];
        } else if (['published_at', 'edited_at'].includes(input)) {
          payload[input] = Format.date(
            values[input],
            Config.get('API_DATETIME_FORMAT')
          );
        } else {
          payload[input] = values[input];
        }
      }
    }

    if (id) {
      // update
      props.update(id, payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    } else {
      // create
      props.create(payload, (success) => {
        if (success) {
          message.success(getI18n().t('feedback_form_success'));
        } else {
          message.error(getI18n().t('feedback_form_error'));
        }
      });
    }
  };

  // form submit handler
  const handleFinish = async (values) => {
    Logger.log('debug', `ResourceNoteForm.handleFinish(###)`);
    if (!props.isSubmitting) {
      await submitData(values);
    }
  };

  // form error handler
  const handleFinishFailed = ({ values, errorFields, outOfDate }) => {
    Logger.log('debug', `ResourceNoteForm.handleFinishFailed(###)`);
    message.error(getI18n().t('feedback_form_error'));
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name);
    }
  };

  const onChangeResource = (e) => {
    const resource = denormalize(
      state.entities.getIn(['resources', e]),
      schema.resource,
      state.entities.toJS()
    );

    if (resource?.care_guide) {
      setPartnershipId(null);
      setCareGuideIds([resource.care_guide?.id]);
    } else if (resource.care_guides.length > 0) {
      const ids = resource.care_guides.map((x) => {
        return x.id;
      });
      setPartnershipId(null);
      setCareGuideIds(ids);
    } else if (resource.partnership) {
      setCareGuideIds([]);
      setPartnershipId(resource.partnership?.id);
    }else{
      setCareGuideIds([]);
      setPartnershipId(null);
    }
    form.setFieldsValue({ profile: null });
  };

  if (redirectTo) {
    return <Redirect to={redirectTo} />;
  }

  return (
    <Translation>
      {(t) => (
        <>
          <div className="resource-note-form">
            <Form
              name="resource_note_form"
              form={form}
              initialValues={data}
              onFinish={handleFinish}
              onFinishFailed={handleFinishFailed}
              validateTrigger="onSubmit"
              {...layout.main}
            >
              <Row gutter={16}>
                <Col xs={24} lg={18}>
                  <Card
                    title={
                      id
                        ? t('resource_note_edit_title')
                        : t('resource_note_add_title')
                    }
                    extra={
                      isLoading ? (
                        <Spin
                          indicator={
                            <LoadingOutlined style={{ fontSize: 20 }} spin />
                          }
                        />
                      ) : null
                    }
                  >
                    <ResourceInput
                      form={form}
                      name="resource_id"
                      label={t('resource_note_resource')}
                      rules={[
                        {
                          required: true,
                          message: t('feedback_validation_required'),
                        },
                      ]}
                      disabled={isLoading || isSubmitting}
                      onChange={onChangeResource}
                    />

                    {careGuideIds.length > 0 ? (
                      <UserCareGuidesInput
                        careGuideIds={careGuideIds}
                        name="profile"
                        label={t('resource_note_profile')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                        form={form}
                        disabled={isLoading || isSubmitting}
                      />
                    ) : partnershipId ? (
                      <UserPartnershipInput
                        dataForm={data}
                        firstLoad={firstLoad}
                        setFirstLoad={setFirstLoad}
                        partnershipId={partnershipId}
                        name="profile"
                        label={t('resource_note_profile')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                        form={form}
                        disabled={isLoading || isSubmitting}
                      />
                    ) : (
                      <UserInput
                        form={form}
                        name="profile"
                        label={t('resource_note_profile')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                        disabled={isLoading || isSubmitting}
                      />
                    )}

                    <div className="form-group">
                      <Form.Item
                        name="published_at"
                        label={t('resource_note_published_at')}
                        rules={[
                          {
                            required: true,
                            message: t('feedback_validation_required'),
                          },
                        ]}
                      >
                        <DatePicker
                          showTime
                          disabled={isLoading || isSubmitting}
                          format={Config.get('DEFAULT_DATETIME_FORMAT')}
                        />
                      </Form.Item>
                    </div>

                    <div className="form-group">
                      <Form.Item
                        name="edited_at"
                        label={t('resource_note_edited_at')}
                        rules={null}
                      >
                        <DatePicker
                          showTime
                          disabled={isLoading || isSubmitting}
                          format={Config.get('DEFAULT_DATETIME_FORMAT')}
                        />
                      </Form.Item>
                    </div>

                    <BodyInput
                      name="body"
                      label={t('resource_note_body')}
                      form={form}
                      defaultValue={form.getFieldValue(['body'])}
                      disabled={isLoading || isSubmitting}
                    />

                    <PublicStatusInput
                      name="public_status"
                      label={t('resource_note_public_status')}
                      disabled={isLoading || isSubmitting}
                    />
                  </Card>
                </Col>

                <Col xs={24} lg={6}>
                  <Affix offsetTop={10}>
                    <Card title={t('form_metadata_header')}>
                      <FormMetadata
                        id={id}
                        isSubmitting={isSubmitting}
                        delete={props.delete.bind(this)}
                        deleteRedirectTo="ResourceNotesScreen"
                        createdAt={props.createdAt}
                        updatedAt={props.updatedAt}
                      />
                    </Card>
                  </Affix>
                </Col>
              </Row>
            </Form>
          </div>
        </>
      )}
    </Translation>
  );
};

export default ResourceNoteForm;

Logger.log('silly', `ResourceNoteForm loaded.`);
